import { useFeatureFlagEnabled, useFeatureFlagVariantKey } from 'posthog-js/react';

/**
 * Every PostHog feature flag the app reads, keyed by the name it has in
 * PostHog. Listing them here keeps a typo from silently reading a flag that
 * doesn't exist (which would just look "off" forever).
 *
 * Add a key here when you create the flag in PostHog, and remove it once the
 * experiment ships and the code no longer branches on it.
 */
export const FLAGS = {
  /** The wide side panel with nothing open: `desk` (control) or `noticeboard`. */
  homePanel: 'home-panel-variant',
} as const satisfies Record<string, string>;

export type FlagKey = (typeof FLAGS)[keyof typeof FLAGS];

/**
 * A boolean flag: a rollout or kill switch. False until flags have loaded,
 * and when analytics is off, so the default experience is always the safe one.
 */
export function useFlag(flag: FlagKey): boolean {
  return useFeatureFlagEnabled(flag) ?? false;
}

/**
 * A multivariate flag, for A/B tests. Returns the variant key ('control',
 * 'test', …) or undefined until flags load. Reading it records the exposure
 * PostHog experiments need, so read it where the variant is actually shown.
 */
export function useVariant(flag: FlagKey): string | undefined {
  const variant = useFeatureFlagVariantKey(flag);
  return typeof variant === 'string' ? variant : undefined;
}
